import { PayoutFormFieldType } from '@ts/billing-publisher.types';

import { ADDRESS_FIELD_KEYS } from './constants';

const normalize = (value: string | undefined): string => (value ?? '').trim().toLowerCase();

// Wise returns `valuesAllowed` for coded fields (notably `address.state` for
// US / CA / AU / BR / MX ...) as `optionsList`. Our billing profile stores the
// state as a code for the US but as free text for every other country, so map
// the stored value onto an allowed option by key ("ON") or by name ("Ontario").
export const matchAllowedOption = (
  field: PayoutFormFieldType,
  rawValue: string | undefined
): string | undefined => {
  const needle = normalize(rawValue);
  if (!needle) return undefined;
  const match = field.optionsList.find(
    (option) => normalize(option.key) === needle || normalize(option.label) === needle
  );
  return match?.key;
};

export type ResolvedBillingAddress = {
  // `address.*` keys that can be sent to Wise as-is for the selected
  // requirement. Only keys present in the requirement are included.
  details: Record<string, string>;
  // Address fields of the requirement whose value could not be derived from the
  // billing address (no allowed-value match, or required but empty). The user
  // has to fill these in explicitly.
  unresolvedFields: PayoutFormFieldType[];
};

export const resolveBillingAddressForRequirement = (
  billingAddressDetails: Record<string, string>,
  requirementFields: PayoutFormFieldType[]
): ResolvedBillingAddress => {
  const details: Record<string, string> = {};
  const unresolvedFields: PayoutFormFieldType[] = [];

  requirementFields.forEach((field) => {
    if (!ADDRESS_FIELD_KEYS.has(field.key)) return;
    const raw = (billingAddressDetails[field.key] ?? '').trim();

    if (field.optionsList.length > 0) {
      const matchedKey = matchAllowedOption(field, raw);
      if (matchedKey) {
        details[field.key] = matchedKey;
      } else {
        unresolvedFields.push(field);
      }
      return;
    }

    if (raw) {
      details[field.key] = raw;
    } else if (field.required) {
      unresolvedFields.push(field);
    }
  });

  return { details, unresolvedFields };
};
